import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import * as AuthSession from 'expo-auth-session'
import { tokenStore } from './tokenStore'
import * as WebBrowser from 'expo-web-browser'
import { AuthContext, type AuthState } from './useAuth'
import { authEnabled, oidcConfig } from './config'
import { setTokenGetter, setUnauthorizedHandler } from './tokenBridge'
import { queryClient } from '../providers/QueryProvider'
import { api } from '../api/client'
import { reportError, setMonitoringUser } from '../lib/monitoring'

WebBrowser.maybeCompleteAuthSession()

const TOKEN_KEY = 'aslife.tokens'
/** Refresh slightly before real expiry to avoid edge-of-expiry 401s. */
const EXPIRY_SKEW_MS = 30_000

type Tokens = { accessToken: string; refreshToken?: string; expiresAt: number }

/**
 * POST на token-endpoint Keycloak с ОДНИМ повтором при сетевом сбое.
 *
 * Зачем повтор: браузер держит keep-alive соединение к `life.asgroup.az`, и протухший сокет
 * роняет первый же POST (`net::ERR_CONNECTION_*` → `TypeError` из fetch). GET Chrome повторяет
 * сам, POST — нет, и одна такая осечка стоила бы пользователю сессии (21.08.2026).
 * Повтор безопасен: в realm `aslife` `revokeRefreshToken=false`, refresh-токен не ротируется
 * и не сгорает от повторного предъявления; повторный ROPC-вход — просто ещё один вход.
 */
async function postToken(body: Record<string, string>): Promise<Response> {
  const endpoint = oidcConfig.issuer.replace(/\/$/, '') + '/protocol/openid-connect/token'
  const init: RequestInit = {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams(body).toString(),
  }
  try {
    return await fetch(endpoint, init)
  } catch {
    // Только сетевой сбой (HTTP-ответ, даже 500, сюда не попадает) — пробуем ещё раз, уже по новому соединению.
    return await fetch(endpoint, init)
  }
}

function toTokens(res: AuthSession.TokenResponse): Tokens {
  const issuedAtMs = (res.issuedAt ?? Math.floor(Date.now() / 1000)) * 1000
  const ttlMs = (res.expiresIn ?? 300) * 1000
  return { accessToken: res.accessToken, refreshToken: res.refreshToken, expiresAt: issuedAtMs + ttlMs }
}

/**
 * OIDC (Authorization Code + PKCE) against Keycloak/Entra, with secure-store
 * persistence and silent refresh. When `authEnabled` is false we run in
 * dev-fallback (no IdP): the API client sends x-debug-user-email instead of a
 * Bearer token and the user is always considered signed in.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [tokens, setTokensState] = useState<Tokens | null>(null)
  const [isLoading, setLoading] = useState(true)
  const [isAuthenticating, setAuthenticating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Аккаунт с висящим required action (обычно временный пароль) ROPC не пускает, его можно
  // довести только браузерным флоу — экран входа переключает кнопку на login() (app/login.tsx).
  const [mustSetPassword, setMustSetPassword] = useState(false)
  // Explicit sign-out, needed for dev-fallback where isAuthenticated would
  // otherwise be hardcoded `true` and logout would have no visible effect.
  const [signedOut, setSignedOut] = useState(false)

  // Latest tokens for the long-lived token getter — avoids stale closures.
  const tokensRef = useRef<Tokens | null>(null)
  const discoveryRef = useRef<AuthSession.DiscoveryDocument | null>(null)
  // Общий refresh на все параллельные запросы: при ротации refresh-токенов в
  // Keycloak второй одновременный refreshAsync получил бы invalid_grant → logout.
  const refreshInFlight = useRef<Promise<string | null> | null>(null)

  const redirectUri = AuthSession.makeRedirectUri({ scheme: 'aslife', path: 'auth/callback' })

  const persist = useCallback(async (next: Tokens | null) => {
    tokensRef.current = next
    setTokensState(next)
    try {
      if (next) await tokenStore.setItem(TOKEN_KEY, JSON.stringify(next))
      else await tokenStore.deleteItem(TOKEN_KEY)
    } catch {
      /* best-effort: a failed write must never crash the app */
    }
  }, [])

  // Discovery is fetched once and cached for the session.
  const getDiscovery = useCallback(async () => {
    if (!discoveryRef.current) {
      discoveryRef.current = await AuthSession.fetchDiscoveryAsync(oidcConfig.issuer)
    }
    return discoveryRef.current
  }, [])

  // Restore a saved session on boot.
  useEffect(() => {
    ;(async () => {
      try {
        const raw = await tokenStore.getItem(TOKEN_KEY)
        if (raw) {
          const parsed = JSON.parse(raw) as Tokens
          tokensRef.current = parsed
          setTokensState(parsed)
        }
      } catch {
        /* ignore corrupt/absent session */
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  const logout = useCallback(async () => {
    // End the Keycloak session server-side (best-effort): invalidate the refresh
    // token so it can't be reused. ROPC sets no browser SSO cookie, so this POST is
    // what actually revokes the session. A network failure must not block local sign-out.
    const current = tokensRef.current
    if (authEnabled && current?.refreshToken) {
      try {
        const endpoint = oidcConfig.issuer.replace(/\/$/, '') + '/protocol/openid-connect/logout'
        await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({
            client_id: oidcConfig.clientId,
            refresh_token: current.refreshToken,
          }).toString(),
        })
      } catch {
        /* best-effort: clear local tokens regardless of the network result */
      }
    }
    await persist(null)
    queryClient.clear()
    setSignedOut(true)
    setError(null)
  }, [persist])

  /**
   * Обновление истёкшего access-токена.
   *
   * Ключевое правило: разлогинивать ТОЛЬКО когда Keycloak сам отверг refresh-токен (4xx —
   * истёк, отозван, сессии больше нет). Сетевой сбой и 5xx означают «не смогли спросить»,
   * а не «токен мёртв»: там мы бросаем ошибку наверх, сессию и токены сохраняем, и следующий
   * запрос попробует снова. До 21.08.2026 здесь стоял общий `catch { logout() }` — моргнувшая
   * сеть выкидывала человека на экран входа с потерей заполненной формы.
   *
   * Запрос идёт напрямую по адресу из issuer (как и парольный вход), без `fetchDiscoveryAsync`:
   * discovery — это ещё один сетевой вызов, чей сбой раньше тоже означал выход.
   */
  const refresh = useCallback(
    async (current: Tokens): Promise<string | null> => {
      if (!current.refreshToken) {
        await logout()
        return null
      }
      const res = await postToken({
        grant_type: 'refresh_token',
        client_id: oidcConfig.clientId,
        refresh_token: current.refreshToken,
      })
      if (!res.ok) {
        if (res.status >= 500) {
          // Keycloak лёг — шумим в мониторинг (как и на входе) и оставляем сессию жить.
          const err = new Error(`Keycloak refresh failed: ${res.status}`)
          reportError(err)
          throw err
        }
        await logout()
        return null
      }
      const json = (await res.json()) as {
        access_token: string
        refresh_token?: string
        expires_in?: number
      }
      const next: Tokens = {
        accessToken: json.access_token,
        // Keycloak может не ротировать refresh-токен — тогда остаётся прежний.
        refreshToken: json.refresh_token ?? current.refreshToken,
        expiresAt: Date.now() + (json.expires_in ?? 300) * 1000,
      }
      await persist(next)
      return next.accessToken
    },
    [persist, logout],
  )

  // Register the token getter + 401 handler (read tokensRef, not stale state).
  useEffect(() => {
    setTokenGetter(async () => {
      if (!authEnabled) return null
      const current = tokensRef.current
      if (!current) return null
      if (current.expiresAt - EXPIRY_SKEW_MS > Date.now()) return current.accessToken
      if (!refreshInFlight.current) {
        refreshInFlight.current = refresh(current).finally(() => {
          refreshInFlight.current = null
        })
      }
      return refreshInFlight.current
    })
    setUnauthorizedHandler(() => {
      if (authEnabled) void logout()
    })
  }, [refresh, logout])

  // Monitoring identity: one effect for the whole auth-state transition instead of
  // separate calls in login/loginWithPassword/logout — this way session restore on
  // boot and logout-via-401 (notifyUnauthorized → logout) are covered by the same
  // path, with a single source of truth. Email is the userId: stable, readable on
  // the dashboard, and the same value dev-fallback already sends via x-debug-user-email.
  // fetchQuery shares the ['me'] cache key with useMe, so this doesn't add a request.
  useEffect(() => {
    const isAuth = authEnabled ? !!tokens : !signedOut
    if (!isAuth) {
      setMonitoringUser(null)
      return
    }
    queryClient
      .fetchQuery({ queryKey: ['me'], queryFn: api.me })
      .then((profile) => setMonitoringUser(profile.email))
      .catch(() => {})
  }, [tokens, signedOut])

  const login = useCallback(async (): Promise<boolean> => {
    setSignedOut(false)
    if (!authEnabled) return true // dev-fallback: already "signed in" via debug header
    setError(null)
    setAuthenticating(true)
    try {
      const discovery = await getDiscovery()
      const request = new AuthSession.AuthRequest({
        clientId: oidcConfig.clientId,
        scopes: oidcConfig.scopes,
        redirectUri,
        responseType: AuthSession.ResponseType.Code,
        usePKCE: true,
      })
      const result = await request.promptAsync(discovery)
      if (result.type !== 'success' || !result.params.code) {
        if (result.type === 'error') setError(result.error?.message ?? 'Не удалось войти')
        return false
      }
      const token = await AuthSession.exchangeCodeAsync(
        {
          clientId: oidcConfig.clientId,
          code: result.params.code,
          redirectUri,
          extraParams: request.codeVerifier ? { code_verifier: request.codeVerifier } : undefined,
        },
        discovery,
      )
      await persist(toTokens(token))
      setMustSetPassword(false) // required action пройден на странице Keycloak
      return true
    } catch (e) {
      reportError(e)
      setError(e instanceof Error ? e.message : 'Ошибка авторизации')
      return false
    } finally {
      setAuthenticating(false)
    }
  }, [getDiscovery, persist, redirectUri])

  // Direct Access Grant (ROPC): credentials entered in our own form are exchanged
  // for tokens directly at Keycloak's token endpoint — no redirect, no IdP page.
  // Same-origin POST (https://10.20.2.80/kc/...), so no CORS. Requires the
  // Keycloak client to have "Direct access grants" enabled.
  const loginWithPassword = useCallback(
    async (username: string, password: string): Promise<boolean> => {
      setSignedOut(false)
      // dev-fallback: no IdP — identity is carried by x-debug-user-email in the API client.
      if (!authEnabled) return true
      setError(null)
      setMustSetPassword(false)
      setAuthenticating(true)
      try {
        const res = await postToken({
          grant_type: 'password',
          client_id: oidcConfig.clientId,
          scope: oidcConfig.scopes.join(' '),
          username,
          password,
        })
        if (!res.ok) {
          const data = (await res.json().catch(() => null)) as
            | { error?: string; error_description?: string }
            | null
          // Пароль ПРИНЯТ, но у аккаунта висит required action (временный пароль): ROPC такой
          // вход завершить не может (Keycloak: «Account is not fully set up»). Неверный пароль
          // даёт другой текст — «Invalid user credentials», поэтому строку можно различать.
          // Дальше — только браузерный флоу, и запускать его должен ЖЕСТ пользователя: popup
          // без жеста браузер заблокирует, поэтому здесь только поднимаем флаг для экрана.
          if (/not fully set up/i.test(data?.error_description ?? '')) {
            setMustSetPassword(true)
            return false
          }
          let msg = 'Не удалось войти'
          if (res.status >= 500) msg = 'Сервер авторизации недоступен'
          else if (data?.error === 'invalid_grant') msg = 'Неверный логин или пароль'
          else if (data?.error === 'unauthorized_client')
            msg = 'Для клиента не включён прямой вход (Direct Access Grants)'
          else if (data?.error_description) msg = data.error_description
          // Мониторим только 5xx (Keycloak лёг) — 4xx вроде invalid_grant это опечатки пользователя, ими дашборд не шумим.
          if (res.status >= 500) reportError(new Error(`Keycloak login failed: ${res.status} ${data?.error ?? ''}`.trim()))
          setError(msg)
          return false
        }
        const json = (await res.json()) as {
          access_token: string
          refresh_token?: string
          expires_in?: number
        }
        await persist({
          accessToken: json.access_token,
          refreshToken: json.refresh_token,
          expiresAt: Date.now() + (json.expires_in ?? 300) * 1000,
        })
        return true
      } catch (e) {
        reportError(e)
        // Сетевой сбой fetch — это `TypeError` с браузерным текстом («Failed to fetch»);
        // показывать его пользователю бессмысленно.
        const network = e instanceof TypeError
        setError(network ? 'Нет связи с сервером авторизации' : e instanceof Error ? e.message : 'Ошибка авторизации')
        return false
      } finally {
        setAuthenticating(false)
      }
    },
    [persist],
  )

  const value = useMemo<AuthState>(
    () => ({
      authEnabled,
      // In dev-fallback the user is authenticated via the debug header until
      // they explicitly sign out (`signedOut`), so logout works without an IdP.
      isAuthenticated: authEnabled ? !!tokens : !signedOut,
      isLoading,
      isAuthenticating,
      error,
      mustSetPassword,
      login,
      loginWithPassword,
      logout,
    }),
    [tokens, signedOut, isLoading, isAuthenticating, error, mustSetPassword, login, loginWithPassword, logout],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
